export interface GraphqlNetworkFailure {
  clientRequestId: string;
  serverRequestId?: string;
  operationName: string;
  operationType: string;
  route?: string;
  category: 'aborted' | 'http' | 'network' | 'offline';
  errorName: string;
  httpStatus?: number;
  durationMs: number;
  attemptCount: number;
  online?: boolean;
}

export interface GraphqlNetworkObservabilityOptions {
  reportFailure: (failure: GraphqlNetworkFailure) => void;
  getRoute?: () => string | undefined;
  getOnline?: () => boolean | undefined;
  createId?: () => string;
  now?: () => number;
}

export interface GraphqlOperationDescriptor {
  operationName: string;
  operationType: string;
}

export interface GraphqlNetworkObservation {
  clientRequestId: string;
  recordAttempt(): void;
  reportError(error: unknown): void;
}

type NetworkErrorLike = {
  name?: unknown;
  statusCode?: unknown;
  response?: {
    status?: unknown;
    headers?: { get?: (name: string) => string | null };
  };
};

const defaultNow = () => performance.now();

function defaultCreateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (value) => {
    const random = Math.floor(Math.random() * 16);
    const nibble = value === 'x' ? random : (random & 0x3) | 0x8;
    return nibble.toString(16);
  });
}

function numberField(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function errorDetails(error: unknown, online?: boolean) {
  const candidate =
    error && typeof error === 'object' ? (error as NetworkErrorLike) : {};
  const httpStatus =
    numberField(candidate.statusCode) ??
    numberField(candidate.response?.status);
  const errorName =
    typeof candidate.name === 'string' ? candidate.name : 'Error';
  const serverRequestId = candidate.response?.headers?.get?.('x-request-id');

  const category =
    online === false
      ? ('offline' as const)
      : errorName === 'AbortError'
        ? ('aborted' as const)
        : httpStatus
          ? ('http' as const)
          : ('network' as const);

  return {
    category,
    errorName,
    httpStatus,
    serverRequestId: serverRequestId ?? undefined,
  };
}

export function createGraphqlNetworkObserver(
  options: GraphqlNetworkObservabilityOptions,
) {
  const createId = options.createId ?? defaultCreateId;
  const now = options.now ?? defaultNow;

  return {
    begin(operation: GraphqlOperationDescriptor): GraphqlNetworkObservation {
      const startedAt = now();
      const clientRequestId = createId();
      let attemptCount = 0;

      return {
        clientRequestId,
        recordAttempt() {
          attemptCount += 1;
        },
        reportError(error: unknown) {
          const online = options.getOnline?.();

          try {
            options.reportFailure({
              clientRequestId,
              operationName: operation.operationName || 'anonymous',
              operationType: operation.operationType,
              route: options.getRoute?.(),
              durationMs: Math.max(0, Math.round(now() - startedAt)),
              attemptCount: Math.max(1, attemptCount),
              online,
              ...errorDetails(error, online),
            });
          } catch {
            // Observability must never change application behavior.
          }
        },
      };
    },
  };
}
